import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowLeft, MapPin, Mail, Linkedin, Brain, Clock } from "lucide-react";
import { fmtSignal, relativeTime } from "@/lib/utils";
import { toast } from "sonner";

export default function ProspectDetailPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [memory, setMemory] = useState([]);

  const loadMemory = () => api.get(`/memory/timeline/${id}`).then((r) => setMemory(r.data.timeline || []));

  useEffect(() => {
    api.get(`/prospects/${id}`).then((r) => setP(r.data.prospect));
    loadMemory();
    // eslint-disable-next-line
  }, [id]);

  const runResearch = async () => {
    setBusy(true); setBrief("");
    try {
      const { data } = await api.post("/agents/run", {
        agent_type: "research",
        input: { company: p.company, prospect_name: p.full_name, title: p.title, prospect_id: p.id },
      });
      setBrief(data.result?.brief || "");
      toast.success("Research brief generated + saved to memory");
      loadMemory();
    } catch { toast.error("Research failed"); }
    setBusy(false);
  };

  const runPersonalize = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/agents/run", {
        agent_type: "personalization",
        input: { prospect: p, channel: "linkedin", step: "connect", tone: "consultative" },
      });
      setMsg(data.result?.message || "");
      toast.success("Outreach drafted + saved to memory");
      loadMemory();
    } catch { toast.error("Personalization failed"); }
    setBusy(false);
  };

  if (!p) return <DashboardLayout title="..."><div className="text-sm text-muted-foreground">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout
      eyebrow={p.company}
      title={p.full_name}
      actions={
        <Link to="/app/prospects" data-testid="prospect-back-link" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> All prospects
        </Link>
      }
    >
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="data-card">
          <div className="label-eyebrow mb-2">Profile</div>
          <div className="text-xl font-display font-semibold tracking-tight">{p.full_name}</div>
          <div className="text-sm text-muted-foreground">{p.title}</div>
          <div className="text-sm mt-1">{p.company}</div>
          <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
            <div className="flex items-center gap-2"><MapPin className="w-3 h-3" />{p.location || "—"}</div>
            <div className="flex items-center gap-2"><Mail className="w-3 h-3" />{p.email || "—"}</div>
            <a href={p.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-foreground" data-testid="prospect-linkedin-link">
              <Linkedin className="w-3 h-3" /> LinkedIn
            </a>
          </div>
          <div className="mt-5 pt-5 border-t border-border">
            <div className="label-eyebrow mb-2">Fit Score</div>
            <div className="text-4xl font-display font-semibold text-signal-500">{p.score}</div>
          </div>
          <div className="mt-5 pt-5 border-t border-border">
            <div className="label-eyebrow mb-2">Signals</div>
            <div className="flex flex-wrap gap-1.5">
              {(p.signals || []).map((s) => (<span key={s} className="signal-pill">{fmtSignal(s)}</span>))}
              {(p.signals || []).length === 0 && <span className="text-xs text-muted-foreground">None detected</span>}
            </div>
          </div>
        </div>

        <div className="data-card lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-eyebrow">AI Research Brief</div>
              <h3 className="font-display font-semibold text-lg mt-1">Account intelligence</h3>
            </div>
            <Button onClick={runResearch} disabled={busy} data-testid="prospect-research-btn" className="rounded-sm">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />{busy ? "Researching..." : "Generate brief"}
            </Button>
          </div>
          {brief ? (
            <pre className="whitespace-pre-wrap text-sm leading-relaxed bg-muted/30 border border-border rounded-sm p-4 max-h-[420px] overflow-y-auto font-sans" data-testid="prospect-research-output">{brief}</pre>
          ) : (
            <div className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-6 text-center">
              Click <span className="font-mono">Generate brief</span> to run the Research Agent on this account.
            </div>
          )}

          <div className="pt-4 border-t border-border flex items-center justify-between">
            <div>
              <div className="label-eyebrow">Personalized opener</div>
              <h3 className="font-display font-semibold text-lg mt-1">AI outreach draft</h3>
            </div>
            <Button onClick={runPersonalize} disabled={busy} data-testid="prospect-personalize-btn" variant="outline" className="rounded-sm">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />{busy ? "Writing..." : "Draft message"}
            </Button>
          </div>
          {msg ? (
            <pre className="whitespace-pre-wrap text-sm leading-relaxed bg-card border border-signal-500/30 rounded-sm p-4 font-sans" data-testid="prospect-personalize-output">{msg}</pre>
          ) : (
            <div className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-6 text-center">
              Generate a personalized LinkedIn connect message grounded in this prospect's signals.
            </div>
          )}
        </div>
      </div>

      <div className="mt-2 text-xs text-muted-foreground">Created {relativeTime(p.created_at)}</div>

      {/* AI Memory Timeline */}
      <section className="mt-8">
        <div className="flex items-center gap-2 mb-3">
          <Brain className="w-4 h-4 text-signal-500" />
          <div className="label-eyebrow">Relationship memory</div>
          <span className="text-[0.65rem] text-muted-foreground font-mono">{memory.length} entries</span>
        </div>
        {memory.length === 0 ? (
          <div className="data-card text-center py-8 text-xs text-muted-foreground">
            No memory yet — run Research or Personalize to build long-term context for this prospect.
          </div>
        ) : (
          <div className="border-l-2 border-signal-500/40 ml-2 space-y-3 pl-5">
            {memory.map((m, i) => (
              <div key={m.id} className="relative" data-testid={`prospect-memory-${i}`}>
                <span className="absolute -left-[1.6rem] top-1.5 w-3 h-3 rounded-full bg-signal-500/30 border-2 border-background"></span>
                <div className="data-card">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="signal-pill">{m.kind}</span>
                    <span className="text-[0.65rem] text-muted-foreground inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{relativeTime(m.ts)}</span>
                  </div>
                  <div className="text-sm font-medium mb-1">{m.summary}</div>
                  {m.source_agent && <div className="text-[0.65rem] text-muted-foreground font-mono">via {m.source_agent} agent</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}
